import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { orderedCategories, type Item } from '@milk/shared';
import { deleteItem, updateItem, useStore } from '../store/store';
import { Dialog, useToast } from './ui';

/** Name, quantity, note, section and which stores it's for. */
export function ItemEditor({ item, storeId, onClose }: { item: Item; storeId: string | null; onClose(): void }) {
  const categories = useStore((s) => s.categories);
  const stores = useStore((s) => s.stores);
  const store = storeId ? stores[storeId] : null;
  const toast = useToast();
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(item.quantity);
  const [note, setNote] = useState(item.note);
  const [categoryId, setCategoryId] = useState(item.categoryId);
  const [storeIds, setStoreIds] = useState<string[]>(item.storeIds);
  const storeList = Object.values(stores).sort((a, b) => a.position - b.position);

  const save = () => {
    const p: Parameters<typeof updateItem>[1] = {};
    if (name.trim() && name.trim() !== item.name) p.name = name.trim();
    if (quantity !== item.quantity) p.quantity = quantity;
    if (note !== item.note) p.note = note;
    if (categoryId !== item.categoryId) p.categoryId = categoryId;
    if (storeIds.slice().sort().join() !== item.storeIds.slice().sort().join()) p.storeIds = storeIds;
    if (Object.keys(p).length) updateItem(item.id, p);
    onClose();
  };

  return (
    <Dialog title={`Edit ${item.name}`} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label className="field">Name<input value={name} onChange={(e) => setName(e.target.value)} required /></label>
        <div className="row">
          <label className="field grow">Quantity<input value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="2 lbs, x3…" /></label>
          <label className="field grow">Section
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {orderedCategories(store, Object.values(categories)).map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
          </label>
        </div>
        <label className="field">Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="The big tub, unsalted…" /></label>
        <fieldset>
          <legend>Buy it at</legend>
          <p className="muted small" style={{ margin: '0 0 8px' }}>{storeIds.length ? 'Only these stores.' : 'Any store (nothing ticked).'}</p>
          <ul className="choices">
            {storeList.map((s) => (
              <li key={s.id}>
                <label className="choice">
                  <input type="checkbox" checked={storeIds.includes(s.id)}
                    onChange={(e) => setStoreIds((l) => (e.target.checked ? [...l, s.id] : l.filter((x) => x !== s.id)))} />
                  {s.name}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
        {categoryId !== item.categoryId && <p className="muted small" style={{ margin: 0 }}>The new section will be remembered for next time.</p>}
        <div className="sheet-actions">
          <button type="button" className="btn danger" onClick={() => {
            const undo = deleteItem(item.id);
            onClose();
            toast(`Removed ${item.name}`, 'info', undo ? { label: 'Undo', run: undo } : undefined);
          }}><Trash2 size={18} aria-hidden /> Remove</button>
          <button className="btn primary">Save</button>
        </div>
      </form>
    </Dialog>
  );
}
